import { Box, Button, Dialog, DialogContent, Typography, Grid, Stack, IconButton, Paper, Chip } from '@mui/material';
import { Close as CloseIcon, SettingsOutlined as AxleIcon, TripOrigin as TireIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import { useLanguage } from '../../../utils/i18n';
import { AxleLivePreview } from '../vehicle-type/AxleBuilder';

const StatCard = ({ icon: Icon, title, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: `${color}.light`,
      bgcolor: `${color}.50`,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 16px rgba(0,0,0,0.05)`,
        borderColor: `${color}.main`
      }
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        bgcolor: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
      }}
    >
      <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h5" color={`${color}.dark`} fontWeight={800} sx={{ lineHeight: 1 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const AxleBuilderDetail = ({ open, onClose, data = null }) => {
  const { t } = useLanguage();

  const getAxlesFromData = (row) => {
    if (!row) return [];
    let parsedAxles = [];
    let configObj = row?.axle_configuration;
    if (typeof configObj === 'string') {
      try {
        configObj = JSON.parse(configObj);
      } catch {}
    }
    if (configObj && Array.isArray(configObj.info)) {
      parsedAxles = configObj.info;
    } else if (Array.isArray(row?.axles)) {
      parsedAxles = row.axles;
    }
    return parsedAxles;
  };

  const axles = getAxlesFromData(data);
  const totalAxle = axles.length;
  let totalTire = 0;

  axles.forEach((axle) => {
    let axleTires = 2; // base tires
    if (axle.is_twin_tire && !axle.is_steering) axleTires = 4;
    totalTire += axleTires;
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
        }
      }}
    >
      {/* Header Gradient */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ letterSpacing: 0.5, mb: 0.5 }}>
            {t('axleBuilder.axleDetail', 'Axle Detail')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Info Section */}
        <Box sx={{ p: 2, bgcolor: '#ffffff' }}>
          <Stack spacing={3}>
            {/* Identity */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                <Chip label={data?.name || '-'} color="primary" sx={{ fontWeight: 800, px: 1, borderRadius: 1.5, fontSize: '0.9rem' }} />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {data?.description || 'No description provided for this layout.'}
                </Typography>
              </Stack>
            </Box>

            {/* Statistics */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StatCard icon={AxleIcon} title="Total Axles" value={totalAxle} color="primary" />
              </Grid>
              <Grid item xs={6}>
                <StatCard icon={TireIcon} title="Total Tires" value={totalTire} color="info" />
              </Grid>
            </Grid>
          </Stack>
        </Box>

        {/* Preview Section */}
        <Box sx={{ bgcolor: '#f8fafc', p: 3, borderTop: '1px solid #e2e8f0' }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'white',
              borderRadius: 3,
              border: '1px dashed #cbd5e1',
              display: 'flex',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Subtle background pattern or styling could go here */}
            <Box sx={{ position: 'relative', zIndex: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <AxleLivePreview axles={axles} />
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      {/* Footer (Optional, can be removed if close button in header is enough, but keeping for UX consistency) */}
      <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#ffffff', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            fontWeight: 600
          }}
        >
          {t('buttonTitle.close', 'Close')}
        </Button>
      </Box>
    </Dialog>
  );
};

export default AxleBuilderDetail;
